import { useCallback, useEffect, useMemo, useRef } from "react";
import { DraftAccount, EditableAccountPatch } from "../draftState";
import { findDuplicateAccountNameIds } from "../useEditorDraftValidation";
import { findWorkspaceDuplicateCodeIds } from "../workspaceHost/workspaceEditorHost";
import { CoaEditorCommand } from "./coaEditorCommand";
import { PendingAddEntry } from "./nestedPendingAdds";
import { NestedPendingAdds } from "./useNestedPendingAdds";

type WithheldField = "name" | "codeText";

const WITHHELD_FIELDS: WithheldField[] = ["name", "codeText"];

interface Params {
    isEnabled: boolean;
    accounts: DraftAccount[];
    duplicateNameIds: Set<string>;
    duplicateCodeIds: Set<string>;
    onOperation?: (command: CoaEditorCommand) => void;
    pendingAdds: NestedPendingAdds;
}

export interface NestedWithheldWrites {
    emitAccountPatch: (params: {
        accountIds: string[];
        patch: EditableAccountPatch;
    }) => void;
    emitPendingAdd: (entry: PendingAddEntry, name: string) => void;
}

const pickField = (
    account: EditableAccountPatch,
    field: WithheldField,
): EditableAccountPatch =>
    field === "name" ? { name: account.name } : { codeText: account.codeText };

const findCollidingIds = ({
    accounts,
    accountIds,
    patch,
    field,
}: {
    accounts: DraftAccount[];
    accountIds: ReadonlySet<string>;
    patch: EditableAccountPatch;
    field: WithheldField;
}): Set<string> => {
    const patched = accounts.map((account) =>
        accountIds.has(account.id) ? { ...account, ...patch } : account,
    );
    const duplicates =
        field === "name"
            ? findDuplicateAccountNameIds(patched)
            : findWorkspaceDuplicateCodeIds(patched);
    return new Set([...accountIds].filter((id) => duplicates.has(id)));
};

const buildAddAccountCommand = (
    entry: PendingAddEntry,
    name: string,
): CoaEditorCommand => ({
    type: "addAccount",
    created: entry.targets.map((target) => ({
        accountId: target.draftId,
        entityId: target.entityId,
        parentGroupId: target.parentGroupId,
        position: target.position,
    })),
    accountType: entry.accountType,
    name,
});

// A colliding name or code stays in the draft, where the island names it, and
// is not sent. Once the draft no longer collides the skipped write goes out
// with the field's current value. Nothing survives a reload: the registry
// dies with the editor content.
export const useNestedWithheldWrites = ({
    isEnabled,
    accounts,
    duplicateNameIds,
    duplicateCodeIds,
    onOperation,
    pendingAdds,
}: Params): NestedWithheldWrites => {
    const withheldRef = useRef(new Map<string, Set<WithheldField>>());

    const emitAccountPatch = useCallback(
        ({
            accountIds,
            patch,
        }: {
            accountIds: string[];
            patch: EditableAccountPatch;
        }) => {
            const idSet = new Set(accountIds);
            for (const field of WITHHELD_FIELDS) {
                if (patch[field] === undefined) {
                    continue;
                }
                const collidingIds = findCollidingIds({
                    accounts,
                    accountIds: idSet,
                    patch,
                    field,
                });
                const cleanIds: string[] = [];
                for (const id of accountIds) {
                    const withheldFields = withheldRef.current.get(id);
                    if (collidingIds.has(id)) {
                        if (withheldFields) {
                            withheldFields.add(field);
                        } else {
                            withheldRef.current.set(id, new Set([field]));
                        }
                        continue;
                    }
                    withheldFields?.delete(field);
                    cleanIds.push(id);
                }
                if (cleanIds.length > 0) {
                    onOperation?.({
                        type: "updateAccount",
                        accountIds: cleanIds,
                        patch: pickField(patch, field),
                    });
                }
            }
        },
        [accounts, onOperation],
    );

    const isPendingAddColliding = useCallback(
        (entry: PendingAddEntry, name: string): boolean => {
            const targetIds = new Set(
                entry.targets.map((target) => target.draftId),
            );
            const collidingNameIds = findCollidingIds({
                accounts,
                accountIds: targetIds,
                patch: { name },
                field: "name",
            });
            return (
                collidingNameIds.size > 0 ||
                [...targetIds].some((id) => duplicateCodeIds.has(id))
            );
        },
        [accounts, duplicateCodeIds],
    );

    const emitPendingAdd = useCallback(
        (entry: PendingAddEntry, name: string) => {
            if (isPendingAddColliding(entry, name)) {
                return;
            }
            pendingAdds.take(entry.targets[0].draftId);
            onOperation?.(buildAddAccountCommand(entry, name));
        },
        [isPendingAddColliding, onOperation, pendingAdds],
    );

    useEffect(() => {
        if (!isEnabled) {
            return;
        }
        const accountById = new Map(
            accounts.map((account) => [account.id, account]),
        );
        for (const [accountId, fields] of withheldRef.current) {
            const account = accountById.get(accountId);
            if (!account) {
                withheldRef.current.delete(accountId);
                continue;
            }
            for (const field of fields) {
                const isStillColliding =
                    field === "name"
                        ? duplicateNameIds.has(accountId)
                        : duplicateCodeIds.has(accountId);
                if (isStillColliding) {
                    continue;
                }
                fields.delete(field);
                onOperation?.({
                    type: "updateAccount",
                    accountIds: [accountId],
                    patch: pickField(account, field),
                });
            }
            if (fields.size === 0) {
                withheldRef.current.delete(accountId);
            }
        }
        for (const entry of pendingAdds.getEntries()) {
            if (entry.kind !== "account") {
                continue;
            }
            const name = accountById.get(entry.targets[0].draftId)?.name ?? "";
            const isWaitingForName = name.length === 0;
            const isColliding = entry.targets.some(
                (target) =>
                    duplicateNameIds.has(target.draftId) ||
                    duplicateCodeIds.has(target.draftId),
            );
            if (isWaitingForName || isColliding) {
                continue;
            }
            pendingAdds.take(entry.targets[0].draftId);
            onOperation?.(buildAddAccountCommand(entry, name));
        }
    }, [
        isEnabled,
        accounts,
        duplicateNameIds,
        duplicateCodeIds,
        onOperation,
        pendingAdds,
    ]);

    return useMemo(
        () => ({ emitAccountPatch, emitPendingAdd }),
        [emitAccountPatch, emitPendingAdd],
    );
};
